import { useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Calendar, ChevronDown } from 'lucide-react'
import type { Season } from './types'

interface SeasonSelectorProps {
  seasons: Season[]
  activeSeason: Season | undefined
  upcomingSeasons: Season[]
  pastSeasons: Season[]
  currentSeason: Season | undefined
  onSeasonSelect: (seasonId: string) => void
  dropdownOpen: boolean
  onDropdownOpenChange: (open: boolean) => void
  canManageGroup: boolean
  onManageLeague: () => void
}

export function SeasonSelector({
  seasons,
  activeSeason,
  upcomingSeasons,
  pastSeasons,
  currentSeason,
  onSeasonSelect,
  dropdownOpen,
  onDropdownOpenChange,
  canManageGroup,
  onManageLeague,
}: SeasonSelectorProps) {
  const mobileRef = useRef<HTMLDivElement>(null)
  const desktopRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const mobileContains = mobileRef.current?.contains(target)
      const desktopContains = desktopRef.current?.contains(target)
      
      if (!mobileContains && !desktopContains) {
        onDropdownOpenChange(false)
      }
    }
    
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen, onDropdownOpenChange])

  const sortedSeasons = [...seasons].sort((a, b) => {
    if (a.status === 'active' && b.status !== 'active') return -1
    if (b.status === 'active' && a.status !== 'active') return 1
    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  })

  if (sortedSeasons.length === 0) {
    return (
      <div className="mb-6 p-8 text-center border-2 border-dashed rounded-lg">
        <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="font-semibold mb-2">No Seasons Yet</h3>
        <p className="text-sm text-muted-foreground">
          This league doesn't have any seasons set up yet.
        </p>
        {canManageGroup && (
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={onManageLeague}
          >
            Set Up Seasons
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="mb-6">
      {/* Mobile: Single dropdown for all seasons */}
      <div className="sm:hidden relative" ref={mobileRef}>
        <button
          onClick={() => onDropdownOpenChange(!dropdownOpen)}
          className="flex items-center justify-between gap-2 w-full px-3 py-2.5 bg-muted rounded-lg text-left"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium truncate">{currentSeason?.name || 'Select Season'}</span>
            {currentSeason && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${
                currentSeason.status === 'active' 
                  ? 'bg-green-500/20 text-green-700 dark:text-green-400'
                  : currentSeason.status === 'upcoming'
                    ? 'bg-blue-500/20 text-blue-700 dark:text-blue-400'
                    : 'bg-muted-foreground/20 text-muted-foreground'
              }`}>
                {currentSeason.status === 'active' ? 'Current' : currentSeason.status === 'upcoming' ? 'Upcoming' : 'Complete'}
              </span>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform shrink-0 ${dropdownOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {/* Mobile Dropdown */}
        {dropdownOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-lg z-50 overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-150">
            {/* Active Season */}
            {activeSeason && (
              <>
                <div className="px-3 py-1.5 border-b bg-muted/30">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Current</p>
                </div>
                <button
                  onClick={() => onSeasonSelect(activeSeason.seasonId)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left transition-colors hover:bg-accent ${
                    currentSeason?.seasonId === activeSeason.seasonId ? 'bg-accent' : ''
                  }`}
                >
                  <span className="truncate font-medium">{activeSeason.name}</span>
                </button>
              </>
            )}
            
            {/* Upcoming Seasons */}
            {upcomingSeasons.length > 0 && (
              <>
                <div className={`px-3 py-1.5 bg-muted/30 ${activeSeason ? 'border-t' : ''} border-b`}>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Upcoming</p>
                </div>
                {upcomingSeasons.map(season => (
                  <button
                    key={season.seasonId}
                    onClick={() => onSeasonSelect(season.seasonId)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-left transition-colors hover:bg-accent ${
                      currentSeason?.seasonId === season.seasonId ? 'bg-accent' : ''
                    }`}
                  >
                    <span className="truncate">{season.name}</span>
                    {season.signupsOpen && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-medium shrink-0">
                        Open
                      </span>
                    )}
                  </button>
                ))}
              </>
            )}
            
            {/* Past Seasons */}
            {pastSeasons.length > 0 && (
              <>
                <div className={`px-3 py-1.5 bg-muted/30 ${activeSeason || upcomingSeasons.length > 0 ? 'border-t' : ''} border-b`}>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Previous</p>
                </div>
                <div className="max-h-[200px] overflow-y-auto">
                  {pastSeasons.map(season => (
                    <button
                      key={season.seasonId}
                      onClick={() => onSeasonSelect(season.seasonId)}
                      className={`w-full px-3 py-2.5 text-sm text-left transition-colors hover:bg-accent truncate ${
                        currentSeason?.seasonId === season.seasonId ? 'bg-accent' : ''
                      }`}
                    >
                      {season.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Desktop: Pill style with dropdown for other seasons */}
      <div className="hidden sm:flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
        {/* Current/Active Season Button */}
        {activeSeason ? (
          <button
            onClick={() => onSeasonSelect(activeSeason.seasonId)}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              currentSeason?.seasonId === activeSeason.seasonId
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {activeSeason.name}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-700 dark:text-green-400 font-medium">Current</span>
          </button>
        ) : sortedSeasons[0] && (
          <button
            onClick={() => onSeasonSelect(sortedSeasons[0].seasonId)}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              currentSeason?.seasonId === sortedSeasons[0].seasonId
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {sortedSeasons[0].name}
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted-foreground/20 text-muted-foreground font-medium">Latest</span>
          </button>
        )}
        
        {/* Other Seasons Dropdown */}
        {(upcomingSeasons.length > 0 || pastSeasons.length > 0) && (
          <div className="relative" ref={desktopRef}>
            <button
              onClick={() => onDropdownOpenChange(!dropdownOpen)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                currentSeason && currentSeason.seasonId !== activeSeason?.seasonId
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {currentSeason && currentSeason.seasonId !== activeSeason?.seasonId ? (
                <>
                  {currentSeason.name}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    currentSeason.status === 'upcoming' 
                      ? 'bg-blue-500/20 text-blue-700 dark:text-blue-400' 
                      : 'bg-muted-foreground/20 text-muted-foreground'
                  }`}>
                    {currentSeason.status === 'upcoming' ? 'Upcoming' : 'Complete'}
                  </span>
                </>
              ) : (
                'Other'
              )}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Desktop Dropdown */}
            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-1 min-w-[220px] bg-popover border rounded-lg shadow-lg z-50 overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-150">
                {/* Upcoming */}
                {upcomingSeasons.length > 0 && (
                  <>
                    <div className="px-3 py-1.5 border-b bg-muted/30">
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Upcoming</p>
                    </div>
                    <div className="py-1">
                      {upcomingSeasons.map(season => (
                        <button
                          key={season.seasonId}
                          onClick={() => onSeasonSelect(season.seasonId)}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left transition-colors hover:bg-accent ${
                            currentSeason?.seasonId === season.seasonId ? 'bg-accent' : ''
                          }`}
                        >
                          <span className="truncate">{season.name}</span>
                          {season.signupsOpen && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 font-medium shrink-0">
                              Open
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                
                {/* Previous */}
                {pastSeasons.length > 0 && (
                  <>
                    <div className={`px-3 py-1.5 bg-muted/30 ${upcomingSeasons.length > 0 ? 'border-t' : ''} border-b`}>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Previous</p>
                    </div>
                    <div className="py-1 max-h-[180px] overflow-y-auto">
                      {pastSeasons.map(season => (
                        <button
                          key={season.seasonId}
                          onClick={() => onSeasonSelect(season.seasonId)}
                          className={`w-full px-3 py-2 text-sm text-left transition-colors hover:bg-accent truncate ${
                            currentSeason?.seasonId === season.seasonId ? 'bg-accent' : ''
                          }`}
                        >
                          {season.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
